if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();
      }

      connectedCallback() {
        // Defer so children are guaranteed to be in the DOM when this element is
        // injected dynamically (e.g. via innerHTML in autocomplete results).
        Promise.resolve().then(() => this._init());
      }

      _init() {
        if (this._initialized) return;
        this._initialized = true;

        this.form = this.querySelector('form');
        if (!this.form) return;

        this.form.querySelector('[name=id]').disabled = false;

        this.onSubmit = this.handleClickOnSubmit.bind(this);
        this.onDocumentClick = this.handleDocumentClick.bind(this);
        this.onOptionClick = this.handleOptionClick.bind(this);
        this.onCloseButtonClick = this.closeVariantSelector.bind(this);
        this.onVariantChanged = this.onSubmitHandler.bind(this);

        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';

        this.form.addEventListener('submit', this.onSubmit);

        if (window.requestIdleCallback) {
          this.idleInitId = window.requestIdleCallback(() => this.initElements());
        } else {
          this.idleInitId = setTimeout(() => this.initElements(), 1);
        }
      }

      initElements() {
        this.productCardWrapper = this.closest('.js-product-card-wrapper');
        this.variantSelector = this.productCardWrapper?.querySelector('.variant-selector');
        this.cardElement = this.productCardWrapper?.querySelector('.card--card');
        this.closeBtn = this.variantSelector?.querySelector('.selector__close');
        this.variantOptions = this.variantSelector?.querySelectorAll('.selector__menu > li');
        this.productFormVariantInput = this.productCardWrapper?.querySelector('.product-variant-id');
        this.bodyLayer = document.querySelector('.body-layer');

        // Contextual elements (PDP/sticky)
        const productContainer = this.closest('.product');
        this.variantRadios = productContainer?.querySelector('variant-radios');
        this.dropdownSelect = productContainer?.querySelector('dropdown-selects');
      }

      disconnectedCallback() {
        if (window.cancelIdleCallback && this.idleInitId) {
          window.cancelIdleCallback(this.idleInitId);
        } else if (this.idleInitId) {
          clearTimeout(this.idleInitId);
        }

        this.form.removeEventListener('submit', this.onSubmit);
        this.variantRadios?.removeEventListener('variant:changed', this.onVariantChanged);
        this.cleanupVariantSelectorListeners();
      }

      handleClickOnSubmit(evt) {
        evt.preventDefault();

        // Refresh dynamic elements if not cached (safety for dynamic content)
        if (!this.variantRadios || !this.dropdownSelect) {
          const productContainer = this.closest('.product');
          this.variantRadios = this.variantRadios || productContainer?.querySelector('variant-radios');
          this.dropdownSelect = this.dropdownSelect || productContainer?.querySelector('dropdown-selects');
        }

        this.productCardWrapper?.classList.add('active-form');

        if (this.submitButton.dataset.hasVariants) {
          // Product card case: Open the size selector dropdown if the product has variants
          evt.stopImmediatePropagation();
          this.onOpenVariantSelector();
        } else if (this.variantRadios && !this.variantRadios.hasAttribute('selected')) {
          // PDP case when no size is selected
          evt.stopImmediatePropagation();
          if (this.closest('.appears-sticky') || this.closest('.floating-add-to-cart')) {
            // If in sticky buy bar -- show size selector
            this.dropdownSelect?.click();

            // Add listener to submit once variant is selected
            this.variantRadios.removeEventListener('variant:changed', this.onVariantChanged);
            this.variantRadios.addEventListener('variant:changed', this.onVariantChanged, { once: true });
          } else {
            // Show an error if no size is selected
            this.variantRadios.setAttribute('has-error', '');
          }
        } else {
          // otherwise proceed with form submission
          this.onSubmitHandler();
        }
      }

      onOpenVariantSelector() {
        this.showVariantSelector();

        this.cleanupVariantSelectorListeners();

        document.addEventListener('click', this.onDocumentClick);
        this.closeBtn?.addEventListener('click', this.onCloseButtonClick);
        this.variantOptions?.forEach((option) => {
          if (option.classList.contains('sold-out')) return;
          option.addEventListener('click', this.onOptionClick);
        });
      }

      cleanupVariantSelectorListeners() {
        document.removeEventListener('click', this.onDocumentClick);
        this.closeBtn?.removeEventListener('click', this.onCloseButtonClick);
        this.variantOptions?.forEach((option) => {
          option.removeEventListener('click', this.onOptionClick);
        });
      }

      closeVariantSelector() {
        this.productCardWrapper?.classList.remove('active-form');
        this.hideVariantSelector();
      }

      showVariantSelector() {
        if (!this.variantSelector) return;
        this.variantSelector.classList.remove('hidden');
        if (!this.querySelector('.submit-message')?.classList.contains('hidden')) {
          this.handleStateClearAfterSubmit();
        }

        if (window.innerWidth < 990) {
          document.body.appendChild(this.variantSelector);
          // make sure the element is placed on the top layer
          this.variantSelector.style['z-index'] = '1000';
          this.bodyLayer.style['z-index'] = '1000';
          this.bodyLayer.classList.remove('hidden');
        }
      }

      handleDocumentClick(event) {
        if (
          this.variantSelector &&
          !this.variantSelector.contains(event.target) &&
          !event.target.closest('dropdown-selects-options')
        ) {
          this.productCardWrapper?.classList.remove('active-form');
          this.hideVariantSelector();
        }
      }

      handleOptionClick(event) {
        if (!this.productFormVariantInput) this.initElements();
        if (!this.productFormVariantInput) return;

        this.productFormVariantInput.value = event.currentTarget.dataset.variantId || event.target.dataset.variantId;
        this.onSubmitHandler();
        this.hideVariantSelector();
      }

      hideVariantSelector() {
        if (!this.variantSelector) return;
        this.variantSelector.classList.add('hidden');
        this.cleanupVariantSelectorListeners();

        if (window.innerWidth < 990) {
          this.cardElement.appendChild(this.variantSelector);
          this.variantSelector.style['z-index'] = '';
          this.bodyLayer.style['z-index'] = '';
          this.bodyLayer.classList.add('hidden');
        }
      }

      handleStateClearAfterSubmit() {
        this.querySelector('.submit-message')?.classList.add('hidden');
        this.querySelector('.default-message')?.classList.remove('hidden');
        this.submitButton.classList.remove('loading-bg-color');
      }

      async onSubmitHandler() {
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading', 'loading-bg-color');
        this.querySelector('.loading-overlay__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id),
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        try {
          const response = await fetch(`${routes.cart_add_url}`, config);
          const data = await response.json();

          if (data.status) {
            this.error = true;
            publish(PUB_SUB_EVENTS.cartError, {
              source: 'product-form',
              productVariantId: formData.get('id'),
              errors: data.errors || data.description,
              message: data.message,
            });
            this.handleErrorMessage(data.description);

            const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
            if (soldOutMessage) {
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButton.querySelector('span').classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
            }
            return;
          } else if (!this.cart) {
            window.location = window.routes.cart_url;
            return;
          }
          // reset error flag after successful add to cart
          this.error = false;

          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'product-form',
            productVariantId: formData.get('id'),
            cartData: data,
            element: this,
          });

          const isCartReco = Boolean(this.closest('.js-cart-reco'));
          const quickAddModal = this.closest('quick-add-modal');
          if (quickAddModal) {
            document.body.addEventListener(
              'modalClosed',
              () => {
                setTimeout(() => {
                  this.cart.renderContents(data, isCartReco);
                  if (isCartReco) this.hideRecoItem();
                });
              },
              { once: true },
            );
            quickAddModal.hide(true);
          } else {
            this.cart.renderContents(data, isCartReco);
            if (isCartReco) this.hideRecoItem();
          }
        } catch (e) {
          this.error = true;
          console.error(e);
        } finally {
          this.submitButton.classList.remove('loading');
          if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
          if (!this.error) {
            this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading-overlay__spinner').classList.add('hidden');

            this.querySelector('.submit-message')?.classList.remove('hidden');
            this.querySelector('.default-message')?.classList.add('hidden');
            setTimeout(() => {
              this.handleStateClearAfterSubmit();
              this.productCardWrapper?.classList.remove('active-form');
            }, 5000);
          } else {
            this.querySelector('.loading-overlay__spinner').classList.add('hidden');
            this.submitButton.removeAttribute('aria-disabled');
          }
        }
      }

      hideRecoItem() {
        const slideElement = this.closest('li') || this.closest('.slider-slide');
        if (slideElement) {
          // Hide visually and flag as added
          slideElement.classList.add('hidden');
          slideElement.dataset.addedToCart = 'true';

          // Move the slide element from the list (ul) to the parent keen-slider-component container.
          // This triggers the built-in childList MutationObserver on the ul element to dynamically
          // recalculate slide layout positions, while keeping the card element in the DOM tree
          // so tracking context (e.g. closest('exponea-reco')) remains evaluatable.
          const keenSlider = slideElement.closest('keen-slider-component');
          if (keenSlider) {
            keenSlider.appendChild(slideElement);
          }
        }
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }
    },
  );
}
